import React, { useEffect, useState } from 'react';
import { Sparkles, ArrowRight, Check, RefreshCw, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from './ui/dialog';
import { secureStorage } from '../utils/secureStorage';

/**
 * IntentQuiz — §8 "Not sure where to start?" · 3-question mini-quiz.
 *
 * Ask the visitor three plain-language questions, then generate a
 * personalised **Your Amanah Plan** checklist that deep-links into the
 * existing tools. Nothing is transmitted; the resulting plan lives in
 * `localStorage` under `amanah_plan` so returning visitors can pick up
 * where they left off (§45).
 *
 *   Q1. Life stage         → weights Zakat / Faraid / Wasiyyah
 *   Q2. Family situation   → weights Faraid + Guardianship
 *   Q3. Immediate priority → picks the first action to surface
 *
 * The output is a small ordered checklist of routes with plain-language
 * copy, e.g.:
 *
 *   ✓ Calculate your Zakat        (auto-checked because Q3 was "Zakat")
 *   ○ Review your assets
 *   ○ Understand your family's inheritance
 *   ○ Prepare your Islamic Will
 */
const PLAN_KEY = 'amanah_plan';

const QUESTIONS = [
  {
    id: 'stage',
    q: 'Which best describes you?',
    options: [
      { id: 'young',    label: 'Young adult, building wealth' },
      { id: 'parent',   label: 'A parent with dependants' },
      { id: 'senior',   label: 'Retired or planning succession' },
      { id: 'family',   label: 'Managing family affairs' },
    ],
  },
  {
    id: 'family',
    q: 'Who is in your immediate family?',
    options: [
      { id: 'spouse_children', label: 'Spouse and children' },
      { id: 'spouse_only',     label: 'Spouse, no children yet' },
      { id: 'parents_only',    label: 'Parents (no spouse, no children)' },
      { id: 'complex',         label: "It's complicated / prefer not to say" },
    ],
  },
  {
    id: 'goal',
    q: 'What would you like to do first?',
    options: [
      { id: 'zakat',       label: 'Calculate my Zakat' },
      { id: 'inheritance', label: 'Understand who inherits from me' },
      { id: 'will',        label: 'Prepare an Islamic Will' },
      { id: 'learn',       label: 'Just learn — 2-minute lessons' },
    ],
  },
];

const buildPlan = (answers) => {
  const { goal, family, stage } = answers;
  // Base plan — always includes the three core tasks. Only the FIRST
  // (auto-checked) step changes based on the immediate goal.
  const plan = [
    {
      id: 'zakat', route: '/zakat/calculator',
      title: 'Calculate your Zakat',
      body: 'Live gold + silver Nisab, all 4 Sunni schools. Private.',
    },
    {
      id: 'inheritance', route: '/inheritance',
      title: 'Understand who inherits from you',
      body: 'Visual family shares — Faraid across all schools.',
    },
    {
      id: 'will', route: '/will',
      title: 'Prepare your Islamic Will',
      body: 'Guided Wasiyyah writer with 1/3 cap enforced.',
    },
    {
      id: 'learn', route: '/learn',
      title: 'Learn in 2 minutes',
      body: 'Bite-sized lessons on Zakat, inheritance, wills.',
    },
  ];

  // Re-order so the "first-action" goal appears at the top.
  const order = ['zakat', 'inheritance', 'will', 'learn'];
  if (goal && order.includes(goal)) {
    order.splice(order.indexOf(goal), 1);
    order.unshift(goal);
  }

  // Weight adjustments (subtle — used to add a tail note).
  const notes = [];
  if (family === 'spouse_children' || stage === 'parent') {
    notes.push('Guardianship is important for your children — include it in your Will.');
  }
  if (stage === 'senior') {
    notes.push('Consider our Succession Planning kit to consolidate everything.');
  }

  return {
    createdAt: new Date().toISOString(),
    answers,
    steps: order.map((k) => plan.find((p) => p.id === k)),
    firstStep: goal || 'zakat',
    notes,
  };
};

const readPlan = () => {
  try {
    const raw = secureStorage.getItem(PLAN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const writePlan = (plan) => {
  try { secureStorage.setItem(PLAN_KEY, JSON.stringify(plan)); } catch { /* ignore */ }
};

export const IntentQuiz = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [plan, setPlan] = useState(null);

  // Load prior plan on open.
  useEffect(() => {
    if (open) {
      const existing = readPlan();
      if (existing) {
        setPlan(existing);
        setAnswers(existing.answers || {});
        setStep(QUESTIONS.length);
      } else {
        setStep(0);
        setAnswers({});
        setPlan(null);
      }
    }
  }, [open]);

  const answer = (qid, oid) => {
    const next = { ...answers, [qid]: oid };
    setAnswers(next);
    if (step + 1 < QUESTIONS.length) {
      setStep(step + 1);
    } else {
      const built = buildPlan(next);
      writePlan(built);
      setPlan(built);
      setStep(QUESTIONS.length);
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setPlan(null);
    try { secureStorage.removeItem(PLAN_KEY); } catch { /* ignore */ }
  };

  const isResult = step >= QUESTIONS.length && plan;
  const currentQuestion = !isResult ? QUESTIONS[step] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="intent-quiz"
        className="max-w-md w-[calc(100vw-2rem)] p-0 bg-white gap-0 rounded-2xl overflow-hidden"
      >
        <DialogHeader className="flex flex-row items-center justify-between gap-3 px-5 py-4 border-b border-stone-200 bg-gradient-to-r from-[#FAF8F5] to-[#F3EFEA]">
          <div className="flex items-start gap-2 min-w-0">
            <Sparkles className="w-4 h-4 text-gold-500 shrink-0 mt-1" />
            <div className="min-w-0">
              <DialogTitle className="font-heading text-[18px] text-[#0D1520] leading-tight">
                {isResult ? 'Your Amanah Plan' : 'Not sure where to start?'}
              </DialogTitle>
              <DialogDescription className="text-[12px] text-stone-500 leading-tight">
                {isResult
                  ? 'A private, personalised checklist. Saved on this device only.'
                  : `Question ${step + 1} of ${QUESTIONS.length}`}
              </DialogDescription>
            </div>
          </div>
          <DialogClose
            data-testid="intent-quiz-close"
            aria-label="Close"
            className="inline-flex w-8 h-8 items-center justify-center rounded-full text-stone-500 hover:bg-stone-100 hover:text-[#0D1520] transition-colors"
          >
            <X className="w-4 h-4" />
          </DialogClose>
        </DialogHeader>

        <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">
          {!isResult && currentQuestion && (
            <div data-testid={`intent-question-${currentQuestion.id}`}>
              {/* Progress dots */}
              <div className="flex items-center gap-1 mb-3" aria-label="Progress">
                {QUESTIONS.map((_, i) => (
                  <span
                    key={`row-${i}`}
                    className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-[#EDF5F1]' : 'bg-stone-200'}`}
                  />
                ))}
              </div>
              <h3 className="font-heading text-[17px] text-[#0D1520] leading-snug mb-3">
                {currentQuestion.q}
              </h3>
              <div className="space-y-2">
                {currentQuestion.options.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => answer(currentQuestion.id, opt.id)}
                    data-testid={`intent-answer-${opt.id}`}
                    className="w-full text-start px-4 py-3 rounded-lg border border-stone-200 hover:border-[#0D4733] hover:bg-[#F3EFEA] text-[14px] text-stone-800 flex items-center justify-between gap-3 transition-colors"
                  >
                    <span>{opt.label}</span>
                    <ArrowRight className="w-4 h-4 text-stone-400 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {isResult && (
            <div data-testid="intent-result">
              <ol className="space-y-2" role="list">
                {plan.steps.map((s, i) => (
                  <li
                    key={s.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      i === 0
                        ? 'border-[#CBD2D9] bg-[#F3EFEA]'
                        : 'border-stone-200 bg-white'
                    }`}
                  >
                    <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                      i === 0 ? 'bg-[#EDF5F1] text-[#0D1520]' : 'bg-stone-100 text-stone-500'
                    }`}>
                      {i === 0 ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => { onOpenChange(false); navigate(s.route); }}
                      data-testid={`intent-plan-${s.id}`}
                      className="flex-1 text-start"
                    >
                      <div className="font-semibold text-[14px] text-[#0D1520]">{s.title}</div>
                      <div className="text-[12px] text-stone-600 leading-snug">{s.body}</div>
                      <div className="mt-1 text-[11px] text-[#4A5568] font-medium inline-flex items-center gap-1">
                        Open <ArrowRight className="w-3 h-3" />
                      </div>
                    </button>
                  </li>
                ))}
              </ol>

              {plan.notes.length > 0 && (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-900 leading-snug">
                  {plan.notes.map((n, i) => (<div key={`row-${i}`}>· {n}</div>))}
                </div>
              )}

              <div className="mt-4 flex items-center justify-between gap-2 pt-3 border-t border-stone-200">
                <div className="text-[11px] text-stone-500">
                  Saved on this device only · never sent to a server
                </div>
                <button
                  type="button"
                  onClick={restart}
                  data-testid="intent-restart"
                  className="inline-flex items-center gap-1.5 text-[12px] text-[#0D1520] hover:text-[#0D1520] font-medium"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Retake
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IntentQuiz;
